<script setup>
import SectionTitle from '@weathernews/btos-vuecomponent/with-onebox-common-elements/src/components/SectionTitle.vue'

// webview flag
const { $isWebViewFlag: isWebViewFlag } = useNuxtApp()

// props
const props = defineProps({
  midrangeData: {
    type: Array,
    default: () => []
  },
  cityInfo: {
    type: Object,
    default: () => {}
  }
})

const midrangeData = deepCopy(props.midrangeData || [])

const oneboxLink = computed(() => {
  if (isWebViewFlag) {
    const lat = props.cityInfo?.lat
    const lon = props.cityInfo?.lon
    if (lat && lon) {
      let nextTo = `weathernews://weathernews.jp/pinpoint?lat=${lat}&lon=${lon}`
      const cityName = props.cityInfo?.cityname || ''
      if (cityName) {
        nextTo += `&name=${encodeURIComponent(cityName)}`
      }
      return nextTo
    }
  } else {
    const cityCode = props.cityInfo?.citycode
    const prefCode = props.cityInfo?.prefcode
    if (cityCode && prefCode) {
      return `https://weathernews.jp/onebox/tenki/${prefCode}/${cityCode}/`
    }
    const lat = props.cityInfo?.lat
    const lon = props.cityInfo?.lon
    if (lat && lon) {
      return `https://weathernews.jp/onebox/tenki/point/${lat}/${lon}/`
    }
  }
  return ''
})
</script>

<template>
  <section class="clothesWeek">
    <div class="heading">
      <SectionTitle title="10日間服装予報" />
      <NuxtLink
        v-if="oneboxLink"
        :to="oneboxLink"
        class="link"
      >詳しい天気予報へ</NuxtLink>
    </div>
    <div class="table">
      <div class="head">
        <div class="content">
          <p>昼間</p>
        </div>
        <div class="content">
          <p>朝晩</p>
        </div>
        <div
          class="data"
          data-temp="high"
        >
          <p>最高</p>
        </div>
        <div
          class="data"
          data-temp="low"
        >
          <p>最低</p>
        </div>
        <div class="data prec" />
      </div>
      <div class="body">
        <div
          v-for="(midrange, index) in midrangeData"
          :key="index"
          class="row"
        >
          <p
            class="date"
            :data-dow="midrange.dayType"
          >
            {{ midrange.dateStr }}<span>{{ midrange.dayStr }}</span>
          </p>
          <div class="content">
            <figure :data-tooltip="midrange.clothes.daytime.name">
              <img
                :src="midrange.clothes.daytime.icon"
                :alt="midrange.clothes.daytime.name"
                width="36"
                height="31"
                loading="lazy"
              >
            </figure>
          </div>
          <div class="content">
            <figure :data-tooltip="midrange.clothes.asaban.name">
              <img
                :src="midrange.clothes.asaban.icon"
                :alt="midrange.clothes.asaban.name"
                width="36"
                height="31"
                loading="lazy"
              >
            </figure>
          </div>
          <div
            class="data"
            data-temp="high"
          >
            <p>{{ midrange.wxdata.MAXT }}<span>℃</span></p>
          </div>
          <div
            class="data"
            data-temp="low"
          >
            <p>{{ midrange.wxdata.MINT }}<span>℃</span></p>
          </div>
          <div class="data">
            <p>{{ midrange.wxdata.POP }}<span>%</span></p>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<style lang="scss" scoped>
.clothesWeek {
  display: flex;
  flex-direction: column;
  gap: 10px;
  .heading {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }
  .area {
    border-left: solid 5px #3569C0;
    padding-left: 5px;
    color: #303030;
    font-size: 18px;
    font-weight: 500;
    line-height: 20px;
  }
  .link {
    padding-right: 25px;
    background-image: url('https://gvs.weathernews.jp/clothes/images/icon/icon_arrow_blue_circle.svg');
    background-position: right center;
    background-repeat: no-repeat;
    background-size: 20px;
    color: #3569C0;
    font-size: 15px;
    font-weight: 500;
    line-height: 20px;
    text-decoration: underline;
  }
  .table {
    display: flex;
    .head {
      display: flex;
      flex-direction: column;
      width: 30px;
      padding-top: 20px;
    }
    .body {
      display: flex;
      flex: 1;
      overflow-x: scroll;
      scrollbar-width: thin;
      scrollbar-color: #3569C0 transparent;
    }
    .row {
      flex: 1;
      min-width: 50px;
      &:nth-child(odd) {
        background-color: #F5F5F5;
        .date {
          border-bottom-color: #fff;
        }
      }
    }
    .date {
      padding: 3px;
      height: 20px;
      color: #000;
      text-align: center;
      font-size: 13px;
      font-weight: 600;
      line-height: 1em;
      border-bottom: solid 1px #f5f5f5;
      &[data-dow="sat"] {
        span {
          color: #6A6CC2;
        }
      }
      &[data-dow="sun"] {
        span {
          color: #E78142;
        }
      }
    }
    .content {
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 3px;
      height: 42px;
      p {
        padding-top: 18px;
        color: #303030;
        text-align: center;
        font-size: 12px;
        font-weight: 500;
        line-height: 1em;
        background-position: center top;
        background-repeat: no-repeat;
        background-size: auto;
      }
      &:first-child {
        p {
          background-image: url('https://gvs.weathernews.jp/clothes/images/icon/icon_day.svg');
        }
      }
      &:nth-child(2) {
        p {
          background-image: url('https://gvs.weathernews.jp/clothes/images/icon/icon_night.svg');
        }
      }
      figure {
        height: 31px;
        img {
          width: auto;
          height: 100%;
        }
      }
    }
    .data {
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 3px;
      height: 22px;
      p {
        font-size: 13px;
        font-weight: 400;
        line-height: 1em;
        text-align: center;
        white-space: nowrap;
        &:is([data-temp="high"] *) {
          color: #F64D00;
        }
        &:is([data-temp="low"] *) {
          color: #3569C0;
        }
        &:is(.body *) {
          font-weight: 600;
        }
      }
      span {
        font-size: 10px;
      }
      &.prec {
        background-image: url('https://gvs.weathernews.jp/clothes/images/icon/icon_prec.svg');
        background-position: center;
        background-repeat: no-repeat;
        background-size: 8px;
      }
    }
  }
  @media (any-hover: hover) {
    .link {
      transition: opacity .2s ease-in-out;
      &:hover {
        opacity: .6;
      }
    }
    .content {
      figure {
        position: relative;
        &::after {
          position: absolute;
          bottom: 75%;
          left: 0;
          display: block;
          padding: 7px 8px 5px;
          background: #000;
          border-radius: 6px;
          font-size: 13px;
          font-weight: 300;
          color: #fff;
          white-space: nowrap;
          content: attr(data-tooltip);
          opacity: 0;
          z-index: 10;
          transition: opacity .2s ease-in-out;
          pointer-events: none;
        }
        &:hover {
          &::after {
            opacity: 1;
          }
        }
      }
    }
    .row {
      &:nth-child(n+7) {
        .content {
          figure {
            &:after {
              left: auto;
              right: 0;
            }
          }
        }
      }
    }
  }
}
.main {
  .clothesWeek {
    @media screen and (min-width: 830px) {
      .table {
        .head {
          width: 40px;
          padding-top: 28px;
        }
        .row {
          min-width: 60px;
        }
        .date {
          padding: 5px;
          height: 28px;
          font-size: 18px;
        }
        .content {
          padding: 5px;
          height: 50px;
          p {
            font-size: 15px;
          }
          figure {
            height: 40px;
          }
        }
        .data {
          height: 30px;
          p {
            font-size: 15px;
            &:is(.body *) {
              font-size: 18px;
            }
          }
          span {
            font-size: 13px;
          }
          &.prec {
            background-size: 10px;
          }
        }
      }
    }
  }
}
</style>
